import React, { useState, useRef, useEffect, useContext } from 'react';
import { useDispatch } from 'react-redux'
import { useSelector } from 'react-redux'
import { useSearchParams } from 'react-router-dom';
import {
  selectAnnotationMethod,
  selectMultipleAnnotations,
  selectSelectedAnnotationIds,
  setAnnotationsForGeometryEdit,
  getAnnotationsForGeometryEdit,
  toggleAnnotationMethod,
  getNewGeometries,
  setValidateModifyOnAnnotation,
  selectDisplayedPhoto,
  selectAnnotationSelectTarget,
  setAnnotationSelectTarget,
} from '../app/interface.js'
import { selectDynamicAnnotationFields, selectUpdateOneFieldOfData, allDescriptionKeys, addCandidateAnnotation, selectCandidateAnnotations, updateAnnotationState, selectAnnotationState, selectAnnotations, updateAnnotation, annotationsApi, selectPresets, setPresets } from '../app/annotations.js'
import AnnotationField from './AnnotationField.jsx'
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
// Ivon-e utils
import { cleanCorruptedAnnotation } from "../app/ivone_utils.js";

import Alert from '@mui/material/Alert';

import './Annotate.css'
import { Box, Button, Divider } from '@mui/material';
import { pos2InspectionCoordSys, selectCoordSys } from '../app/inspection.js';
import { set } from 'ol/transform.js';

function Annotate(props) {
  const [searchParams] = useSearchParams();

  const dispatch = useDispatch()
  const viewId = searchParams.get("viewId")
  const inspectionId = searchParams.get("inspectionId")
  const annotationMethod = useSelector(selectAnnotationMethod)
  const selectTarget = useSelector(selectAnnotationSelectTarget)
  const annotationsFields = useSelector(selectDynamicAnnotationFields)
  const updateOneFieldOfData = useSelector(selectUpdateOneFieldOfData)
  const viewKeys = useSelector(allDescriptionKeys); // The view content (all its keys)
  // The form content is saved in a "annotation" variable ({..., data: {...}})
  // - newAnnotationData: the "local" React state. An update will update the redux state
  // - candidateAnnotation: the Redux state
  const [ newAnnotationData, setNewAnnotationData ] = useState({data: {}})
  const candidateAnnotation = useSelector(selectCandidateAnnotations)
  // State-machine current state:
  const annotationState = useSelector(selectAnnotationState) 
  const [displayAnnotationInformation, setDisplayAnnotationInformation] = useState(null)
  const [annotationStateMessage, setAnnotationStateMessage] = useState("")
  const selectedAnnotationsIds = useSelector(selectSelectedAnnotationIds)
  const annotations = useSelector(selectAnnotations)
  const [selectedAnnotations, setSelectedAnnotations] = useState([])
  const [displaySaveChangeButton, setDisplaySaveChangeButton] = useState(false);
  const updateAnnotationsApi = useSelector(annotationsApi.update)
  const annotationsForGeometryEdit = useSelector(getAnnotationsForGeometryEdit); // The geometry being currently edited
  const [newPresetName, setNewPresetName] = useState(""); // store the next new preset name value
  const selectedCoordSys = useSelector(selectCoordSys)
  const newGeometries = useSelector(getNewGeometries);
  const displayedPhoto = useSelector(selectDisplayedPhoto); // photo displayed in the photo-viewer

  // List of presets objects for annotation creation
  // preset: {
  //  name: string => usef to store the name of the preset displayed on the button
  //  method: string => used to store the annotationMethod value (draw type)
  //  data: {
  //    each field from newAnnotationData with the key from annotationFields keys
  //    to be sure there is only the form content and no other field
  //  }
  // }
  const annotationPresets = useSelector(selectPresets);
  const [displayNewPresetNameError, setDisplayNewPresetNameError] = useState(""); // handle display of error when creating new presets

  // Get presets at start
  useEffect(() => {
    const ls = localStorage.getItem(`presets_inspection_${inspectionId}`)
    if (ls != null){
      const saved_presets = JSON.parse(ls);
      dispatch(setPresets(saved_presets))
    }
  }, [])

  // Save presets
  useEffect(() => {
    localStorage.setItem(`presets_inspection_${inspectionId}`, JSON.stringify(annotationPresets));
  }, [annotationPresets])

  // update selected annotations
  useEffect(() => {
    if (selectedAnnotationsIds && annotations)
      setSelectedAnnotations(annotations.filter(a => selectedAnnotationsIds.includes(a.id)))
  }, [selectedAnnotationsIds, annotations])

  // When the selection or the view changes, update the form:
  useEffect(() => {
    // Selected annotation => set the selected annotation (after its fields corrupted)
    if (selectedAnnotations.length > 0){
      setNewAnnotationData({data: cleanCorruptedAnnotation(viewKeys, selectedAnnotations[0], "default").data});
    }
    // No selected annotation => Let's update the current form to make it valid
    // - it will either make a default annotation
    // - or it will keep the existing form, correcting corrupted values
    else {
      setNewAnnotationData({data: cleanCorruptedAnnotation(viewKeys, newAnnotationData, "default").data});
    }
  }, [viewKeys, selectedAnnotations])

  // Listen to dispatch annotations datas to store
  useEffect(() => {
    if (newAnnotationData !== undefined) {
      dispatch(addCandidateAnnotation({
        inspectionId,
        viewId,
        // En mode photo-viewer, on rattache l’annotation à la photo courante
        photoId: displayedPhoto?.id,
        ...newAnnotationData,
      }))
    }
  }, [newAnnotationData, displayedPhoto])

  const updateAnnotationStateMessage = () => {
    switch(annotationState) {
      case "DefaultForm":
        setDisplayAnnotationInformation(null)
        return "Waiting for the form to be filled";
      case "FormFilling":
        setDisplayAnnotationInformation(null)
        return "Waiting for drawing";
      case "DrawOnGoing":
        setDisplayAnnotationInformation(null)
        return "Drawing ongoing... ESC to cancel last point";
      case "AwaitingCreateValidation":
        setDisplayAnnotationInformation(null)
        return "Saving annotation...";
      case "AnnotationCreated":
        setDisplayAnnotationInformation(<Alert severity="success">Annotation created successfully.</Alert>)
        return "Waiting for the next drawing";
      case "AnnotationRejected":
        setDisplayAnnotationInformation(<Alert severity="error">An error occurred while saving the annotation. Please try again!</Alert>)
        return "Please redraw the annotation";
      case "AnnotationRedrawOnGoing":
        setDisplayAnnotationInformation(null)
        return "Waiting for drawing";
      case "AnnotationsTranslationOnGoing":
        setDisplayAnnotationInformation(null)
        return "Waiting for translation"
      case "AnnotationsTranslateModalOnGoing":
        setDisplayAnnotationInformation(null)
        return "Set X/Y offset or measure with two clicks"
      case "AnnotationsTranslateByTwoClicksOnGoing":
        setDisplayAnnotationInformation(null)
        return "Click first point, then second point"
      case "AnnotationsRemovePointsOnGoing":
        setDisplayAnnotationInformation(null)
        return "Drag a box to remove vertices. Selected lines and polygons only, or any annotation when nothing is selected."
      case "AnnotationEdited":
        setDisplayAnnotationInformation(<Alert severity="success">Annotation edited successfully.</Alert>)
        return "Waiting for drawing";
      case "AnnotationEditionRejected":
        setDisplayAnnotationInformation(<Alert severity="success">An error occurred while editing the annotation. Please try again!</Alert>)
        return "Waiting for drawing";
      case "SelectionOnGoing":
        setDisplayAnnotationInformation(null)
        return "Waiting for selection";
      case "EditOnGoing":
          setDisplayAnnotationInformation(null)
          return "Waiting for annotaiton edition";
      case "TextLabelOnGoing":
        setDisplayAnnotationInformation(null)
        return "Click on the map to place a label";
      default:
        setDisplayAnnotationInformation(null)
        return "Waiting for the form to be filled";
    }
  }

  // When the Annotation state changes:
  useEffect(() => {
    // Update the message
    setAnnotationStateMessage(
      updateAnnotationStateMessage()
    )
    // trigger by leaving Select
    // used to hide Save changes once the selected annotation form is gone
    if (current_mode() !== "Select" || selectTarget === "text") setDisplaySaveChangeButton(false);
  }, [annotationState, annotationsForGeometryEdit, selectTarget])

  // Returns the current mode ("New", "Select", "Move" or "Remove")
  const current_mode = () => {
    if (
      annotationState === "SelectionOnGoing" ||
      annotationState === "EditOnGoing" ||
      annotationState === "AnnotationRedrawOnGoing" ||
      annotationState === "AnnotationModifyOnGoing" ||
      annotationState === "AnnotationPostVertexEditOnGoing" ||
      annotationState === "AnnotationPreVertexEditOnGoing" ||
      annotationState === "AnnotationsTranslateModalOnGoing" ||
      annotationState === "AnnotationsTranslateByTwoClicksOnGoing" ||
      (annotationState === "DrawOnGoing" && annotationsForGeometryEdit.length > 0)
    ) return "Select";
    else if (annotationState === "AnnotationsTranslationOnGoing") return "Move";
    else if (annotationState === "AnnotationsRemovePointsOnGoing") return "Remove";
    else return "New";
  }

  // set new preset name
  const handleNewPresetChange = (e) => {
    // reset error display on change
    if (displayNewPresetNameError != "")
      setDisplayNewPresetNameError("")
    setNewPresetName(e.target.value)
  }

  // handle preset creation
  const handleCreatePreset = () => {
    // new preset
    const newPreset = {
      method: annotationMethod,
      name: newPresetName,
      data: {}
    }
    // get fields and values from annotationFields and newAnnotationData data
    annotationsFields(newAnnotationData.data).keys.forEach((k) => {
      newPreset.data[k.name] = newAnnotationData.data[k.name];
    })
    //  does not allow name duplicate
    if (annotationPresets.some(p => p.name === newPreset.name)) {
      // display identical name error
      setDisplayNewPresetNameError("This name is already taken")
    } else if (newPresetName.length === 0) {
      // display empty name error
      setDisplayNewPresetNameError("Name can't be empty")
    } else {
      // add preset
      dispatch(setPresets([
        ...annotationPresets,
        newPreset
      ]));
      // reset preset name input
      setDisplayNewPresetNameError("")
      setNewPresetName("")
    }
  }

  // remove preset from the list
  const removePreset = (presetName) => {
    dispatch(setPresets([
      ...annotationPresets.filter(p => p.name !== presetName)
    ]));
  }

  // add preset values to the state handling the form
  const handlePreset = (preset) => {
    // update form value
    setNewAnnotationData({data: cleanCorruptedAnnotation(viewKeys, {data: preset.data}, "default").data});
    // update annotationMethod
    dispatch(toggleAnnotationMethod({id: preset.method}))
  }

  // trigger by a redraw
  // used to keep Point, Polygon and Line available so the new geometry can change type
  const redrawingGeometry = annotationState === "AnnotationRedrawOnGoing"
    || (annotationState === "DrawOnGoing" && annotationsForGeometryEdit.length > 0);

  return <>
    <form >
    <div className="annotation-type">
      <div className="mode-selection" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
        <div className="mode-selection-buttons">
          <div
            className={(current_mode() === "New" ? "active" : "") + " toggle-button"}
            title="Keyboard shortcut: press 'n' for New"
            onClick={() => {
            dispatch(updateAnnotationState("FormFilling"))
            dispatch(selectMultipleAnnotations([]))
            dispatch(setAnnotationsForGeometryEdit([]))
          }}>
            New <span className="mode-shortcut">(n)</span>
          </div>

          <div className={(current_mode() === "Select" ? "active" : "") + " toggle-button"} onClick={() => {
            dispatch(updateAnnotationState("SelectionOnGoing"))
            dispatch(selectMultipleAnnotations([]))
            dispatch(setAnnotationsForGeometryEdit([]))
          }}>
            Select <span className="mode-shortcut">(s)</span>
          </div>

          <div
            className={(current_mode() === "Move" ? "active" : "") + " toggle-button"}
            title="Keyboard shortcut: press 'm' for Move"
            onClick={() => {
              dispatch(selectMultipleAnnotations([]))
              dispatch(setAnnotationsForGeometryEdit([]))
              dispatch(updateAnnotationState("AnnotationsTranslationOnGoing"));
            }}
          >
            Move <span className="mode-shortcut">(m)</span>
          </div>
        </div>
        <div className="select-tool-line">
          <div
            className={(current_mode() === "Remove" ? "active" : "") + " toggle-button"}
            title="Keyboard shortcut: press 'r' for Remove points"
            onClick={() => {
              dispatch(setAnnotationsForGeometryEdit([]))
              dispatch(updateAnnotationState("AnnotationsRemovePointsOnGoing"));
            }}
          >
            Partial remove <span className="mode-shortcut">(r)</span>
          </div>
        </div>
      </div>

      <Divider />
      <div>
        {(current_mode() === "New" || redrawingGeometry) && (
          <>
            <div className={(annotationState !== "TextLabelOnGoing" && annotationMethod === "point" ? "active" : "") + " toggle-button"} onClick={() => {
              if (!redrawingGeometry) dispatch(updateAnnotationState("FormFilling"))
              dispatch(toggleAnnotationMethod({id: "point"}))
            }}>
              Point
            </div>
            <div className={(annotationState !== "TextLabelOnGoing" && annotationMethod === "polygon" ? "active" : "") + " toggle-button"} onClick={() => {
              if (!redrawingGeometry) dispatch(updateAnnotationState("FormFilling"))
              dispatch(toggleAnnotationMethod({id: "polygon"}))
            }}>
              Polygon
            </div>
            {/* <div className={annotationMethod === "square" ? "active" : ""} onClick={() => dispatch(toggleAnnotationMethod({id: "square"}))} >
              <FontAwesomeIcon icon={faSquare}/> Square
            </div> */}
            <div className={(annotationState !== "TextLabelOnGoing" && annotationMethod === "line" ? "active" : "") + " toggle-button"} onClick={() => {
              if (!redrawingGeometry) dispatch(updateAnnotationState("FormFilling"))
              dispatch(toggleAnnotationMethod({id: "line"}))
            }}>
              Line
            </div>
            {current_mode() === "New" && (
            <div
              className={(annotationState === "TextLabelOnGoing" ? "active" : "") + " toggle-button"}
              title="Keyboard shortcut: press 'l' for Label"
              onClick={() => {
                dispatch(selectMultipleAnnotations([]))
                dispatch(setAnnotationsForGeometryEdit([]))
                dispatch(updateAnnotationState("TextLabelOnGoing"))
              }}
            >
              Label <span className="mode-shortcut">(l)</span>
            </div>
            )}
          </>
        )}
        {current_mode() === "Select" && (
          <>
            <div className="select-tool-line">
              <button
                type="button"
                role="switch"
                aria-checked={selectTarget === "text"}
                className={"text-target-switch" + (selectTarget === "text" ? " on" : "")}
                title="Keyboard shortcut: press 'l' for Label"
                onClick={() => {
                  dispatch(setAnnotationSelectTarget(selectTarget === "text" ? "annotation" : "text"))
                  dispatch(selectMultipleAnnotations([]))
                  dispatch(setAnnotationsForGeometryEdit([]))
                  dispatch(updateAnnotationState("SelectionOnGoing"))
                }}
              >
                <span className="text-target-switch-track" aria-hidden="true">
                  <span className="text-target-switch-thumb" />
                </span>
                <span>Label <span className="mode-shortcut">(l)</span></span>
              </button>
            </div>
            {selectedAnnotationsIds.length > 0 && (
            <div className="select-tool-line">
              <div
                className={(annotationState === "AnnotationsTranslateModalOnGoing" || annotationState === "AnnotationsTranslateByTwoClicksOnGoing" ? "active" : "") + " toggle-button"}
                title="Keyboard shortcut: press 't' for Translate"
                onClick={() => {
                  dispatch(updateAnnotationState("AnnotationsTranslateModalOnGoing"));
                }}
              >
                Translate (t)
              </div>
              {selectTarget === "annotation" && selectedAnnotations.length > 0 && (
                <>
                  <div
                    className={(annotationState === "AnnotationRedrawOnGoing" || (annotationState === "DrawOnGoing" && annotationsForGeometryEdit.length > 0) ? "active" : "") + " toggle-button" + (selectedAnnotationsIds.length === 1 ? "" : " disabled")}
                    aria-disabled={selectedAnnotationsIds.length !== 1}
                    onClick={() => {
                      if (selectedAnnotationsIds.length !== 1) return;
                      dispatch(setAnnotationsForGeometryEdit([selectedAnnotations[0].id]));
                      dispatch(updateAnnotationState("AnnotationRedrawOnGoing"));
                      dispatch(toggleAnnotationMethod({ id: selectedAnnotations[0].geometryName }));
                    }}
                  >
                    Redraw
                  </div>
                  <div
                    className={(annotationState === "AnnotationModifyOnGoing" ? "active" : "") + " toggle-button" + (selectedAnnotationsIds.length === 1 ? "" : " disabled")}
                    aria-disabled={selectedAnnotationsIds.length !== 1}
                    onClick={() => {
                      if (selectedAnnotationsIds.length !== 1) return;
                      dispatch(setAnnotationsForGeometryEdit([selectedAnnotations[0].id]));
                      dispatch(updateAnnotationState("AnnotationModifyOnGoing"));
                    }}
                  >
                    Modify
                  </div>
                </>
              )}
            </div>
            )}
          </>
        )}
        {
          current_mode() === "Select" &&
          selectTarget === "annotation" &&
          selectedAnnotations.length === 1 &&
          selectedAnnotations[0].geometryName === "line" && (
          <div className="partial-redraw-row">
            <div
              className={(annotationState === "AnnotationPreVertexEditOnGoing" ? "active" : "") + " toggle-button"}
              title="Partial Redraw Towards A"
              onClick={() => {
                dispatch(setAnnotationsForGeometryEdit([selectedAnnotations[0].id]));
                dispatch(updateAnnotationState("AnnotationPreVertexEditOnGoing"));
              }}
            >
              Towards A
            </div>
            <div
              className={(annotationState === "AnnotationPostVertexEditOnGoing" ? "active" : "") + " toggle-button"}
              title="Partial Redraw Towards B"
              onClick={() => {
                dispatch(setAnnotationsForGeometryEdit([selectedAnnotations[0].id]));
                dispatch(updateAnnotationState("AnnotationPostVertexEditOnGoing"));
              }}
            >
              Towards B
            </div>
          </div>
        )}
      </div>
      {/* button to handle save of an updated annotation using modify interaction */}
      <div className="create-annotation-btn" style={{display: annotationState === "AnnotationModifyOnGoing" ? "block" : "none"}} >
            <button onClick={(e) => {
              // prevent reload
              e.preventDefault();
              // trigger modify save in mapwrapper useEffect
              dispatch(setValidateModifyOnAnnotation(true))
            }}>
              Save Modified Geometry
            </button>
          </div>
    </div>

    <div className="annotation-form">
      {((annotationState === "SelectionOnGoing" && selectTarget !== "text" && selectedAnnotationsIds.length === 1)
      || (annotationState !== "TextLabelOnGoing" && annotationState !== "SelectionOnGoing" && annotationState !== "EditOnGoing" && annotationState !== "AnnotationsTranslationOnGoing" && annotationState !== "AnnotationsTranslateByTwoClicksOnGoing" && annotationState !== "AnnotationsTranslateModalOnGoing" && annotationState !== "AnnotationsRemovePointsOnGoing") && newAnnotationData !== undefined) &&
        <>
        {annotationsFields(newAnnotationData.data).keys.map((af) => {
          return <AnnotationField 
            key={"field_" + af.title}
            handleChange={(v) => {
              const {updatedData, keysChanged} = updateOneFieldOfData(newAnnotationData.data, af.name, v);
              const newData = {
                ...newAnnotationData,
                data: {
                  ...newAnnotationData.data,
                  ...updatedData
                }
              }
              // update react state annotation datas
              setNewAnnotationData({data: cleanCorruptedAnnotation(viewKeys, newData, "default").data});
              if (current_mode() === "Select" && selectTarget !== "text") {
                let isDifferent = false
                // comparing orignal selection data and data from the form
                annotationsFields(newData.data).keys.map((af) => {
                  if (newData.data[af.name] !== undefined && selectedAnnotations[0].data[af.name] !== newData.data[af.name])
                    isDifferent = true;
                })                
                // display save changes button if content is different
                setDisplaySaveChangeButton(isDifferent);
              } else {
                // updated annotationState
                dispatch(updateAnnotationState("FormFilling"))
              }
            }}
            field={af}
            // form is disbaled when waiting for backend answer or on annotation geometry redraw
            disabled={annotationState === "AwaitingCreateValidation" 
            || annotationState === "AnnotationRedrawOnGoing"
            || annotationState === "DrawOnGoing"}
            currentValue={newAnnotationData.data[af.name]}
          />
        })}
        { selectedAnnotations.length === 1 &&
          Object.entries(pos2InspectionCoordSys(selectedCoordSys, selectedAnnotations[0].position)).map(([k, v]) => {
            if (k === "area" || k === "length") {
              return <React.Fragment key={k}>
                <h5 style={{margin: 0}} className="title">{k.charAt(0).toUpperCase() + k.slice(1)}</h5>
                <input value={`${v.value.toFixed(3)} ${v.unit}${k === "area" ? '²' : ''}`} disabled/>
              </React.Fragment>
            } else {
              return <React.Fragment key={k}></React.Fragment>
            }
          })
        }
        </>
      }
      {/* displayed only with on select when a selection is made */}
      { (current_mode() === "Select" && selectTarget !== "text" && selectedAnnotationsIds.length === 1 && newAnnotationData) &&
        <div className="create-annotation-btn" style={{display: displaySaveChangeButton ? "block" : "none"}} >
          <button onClick={(e) => {
            // prevent reload
            e.preventDefault();
            // update annotation in the DB
            updateAnnotationsApi({
              ids: selectedAnnotationsIds,
              data: {
                ...newAnnotationData.data
              },
              inspectionId
            }, (result) => {
              setDisplaySaveChangeButton(false)
              if (result  !== null)
                // update annotations in redux state
                dispatch(updateAnnotation(result[0]));
            })
          }}>
            Save changes
          </button>
        </div>
      }
      <div style={{display: "flex", flexWrap: "wrap", padding: "10px 0px"}}>
        <h5 className="title">Status <span title="Steps:
1. Fill the form
2. Select the drawing geometry type
3. Click on the map to start drawing
4. Double click to terminate the drawing and save the annotation
The annotation will be saved automatically after terminating the drawing, please be sure to fill the form beforehand.
">&#9432;</span>: </h5>
        <span style={{fontSize: "14px", margin: "3px"}}>
          {annotationStateMessage}
        </span>
      </div>
      <div className="create-annotation-alert">
        {displayAnnotationInformation}
      </div>
    </div>
  </form>
  { current_mode() === "New" &&
    <>
      <div style={{"paddingBottom": "85px"}}>
        <div style={{ display: 'flex', flexWrap:"wrap"}}>
          <h5 className="title" style={{width: "100%", padding: "10px 0px", margin: 0}}>Annotation presets</h5>
          <input
            className="sidebar-input"
            label="New preset name"
            value={newPresetName}
            onChange={handleNewPresetChange}
            placeholder="New preset name"
          />
          <Button onClick={handleCreatePreset}>
            <AddIcon sx={{bgcolor: 'var(--selected-bgcolor)', color: 'black'}} />
          </Button>
        </div>
        { displayNewPresetNameError.length > 0 &&
          <p style={{color: "red"}} >{displayNewPresetNameError}</p>
        }
        <Box component="div" sx={{ overflow: "auto", height: "200px" }}>
          <ul style={{listStyle: "none"}} >
            { annotationPresets.length > 0 &&
              annotationPresets.map(p => 
                <li style={{marginBottom: "5px"}}>
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Button sx={{bgcolor: 'var(--selected-bgcolor)', color: 'var(--selected-color)'}} onClick={() => {handlePreset(p)}}>
                      {p.name}
                    </Button>
                    <Button onClick={() => {removePreset(p.name)}}>
                      <ClearIcon sx={{ color: "red" }} />
                    </Button>
                  </Box>
                </li>
              )
            }
          </ul>
        </Box>
      </div>
    </>
  }
  </>
}

export default Annotate;